import { type AxiosResponse } from 'axios';
import { push, replace } from 'connected-react-router';
import { type LocationDescriptorObject } from 'history';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { reset, setSubmitFailed, startSubmit } from 'redux-form';
import {
  all,
  call,
  debounce,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
  type ForkEffect,
  type SagaReturnType,
} from 'redux-saga/effects';

import type APIAddress from '~/types/APIAddress';
import type Address from '~/types/Address';
import type CarrierAccount from '~/types/CarrierAccount';
import type Order from '~/types/Order';
import type Shipment from '~/types/Shipment';

import * as addressActions from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import * as ordersActions from '~/actions/orders.actions';
import { updateBatchEntriesAddress as updateBatchEntriesAddressAction } from '~/actions/orders.actions';
import { clearRates, fetchShipment } from '~/actions/shipments.actions';
import { addSnackbarMessage } from '~/actions/snackbar.actions';
import { setIdVerificationStatus } from '~/actions/user.actions';
import { RETURN_ADDRESS_KIND, SENDER_ADDRESS_KIND } from '~/constants/addressBook';
import { CANADA_POST, DHL_EXPRESS, ROYAL_MAIL_SF, UPS } from '~/constants/carriers';
import { countries, type CountryIso2 } from '~/constants/countries';
import { UNEXPECTED_ERROR } from '~/constants/errors/apiMessageErrorCodes';
import { PARCEL_FORM_NAME } from '~/constants/form';
import { fetchOrdersV2 } from '~/modules/orders/services/ordersV2.service';
import { type OrdersV2Response } from '~/modules/orders/types/ordersV2.type';
import { isOrderShipmentRateable } from '~/modules/orders/utils/ordersV2.util';
import { buildOrderTableRowKey } from '~/pages/Orders/Blackbeard/types/types';
import { type PreCheckoutSteps } from '~/reducers/orders/orders.reducer';
import * as addressSagas from '~/sagas/address/address.saga';
import {
  findResetBatchEntries,
  getBatchEntriesIdsToUpdate,
  orderSelectionToBatchEntries,
} from '~/sagas/batch-entry-utils';
import { refreshUser } from '~/sagas/user/user.saga';
import {
  getIncompleteRegStatusFromRates,
  getUnpurchasedBatchEntriesOrderSelectionWithExpiredShipmentDate,
} from '~/selectors/batch-entry.composite.selectors';
import {
  getBatchEntriesByIds,
  getBatchEntriesByOrderObjectIds,
  getBatchEntryById,
  getBatchEntryData,
} from '~/selectors/batch-entry.selectors';
import { getCarrierAccountsObjectIdMap } from '~/selectors/carrier-accounts.selectors';
import {
  getLastOrderId,
  getOrderSuccessfulTransactionIds,
  getOrders,
  getOrdersData,
  getSelectedOrders,
  getTotalOrders,
} from '~/selectors/orders.selectors';
import { getLocation, getParsedQS, getPathname, getQS } from '~/selectors/router.selectors';
import { getShipmentIsLoading, getShipmentsData } from '~/selectors/shipments.selectors';
import { hasStoreConnected } from '~/selectors/stores.selectors';
import { getTransactionsData } from '~/selectors/transactions.selectors';
import {
  getIsIDVRequired,
  getIsUserAddressValid,
  getRequiresEmailVerification,
  userHasValidBilling,
  userIsBillingBlocked,
  userIsHighFraudRisk,
} from '~/selectors/user.selectors';
import * as ordersService from '~/services/orders.service';
import { emailNotify } from '~/services/transactions.service';
import { getVerificationStatus } from '~/services/user.service';
import { getOrders as getWebOrders } from '~/services/web-orders.service';
import { sanitizeAddress } from '~/types/Address';
import { type JSONBusinessRule } from '~/types/BusinessRule';
import { RegStatus, type RegStatusByCarrierAccount } from '~/types/CarrierAccountRegStatus';
import * as IDVerification from '~/types/IDVerification';
import * as order from '~/types/Order';
import {
  batchEntryExtendedFromJSON,
  partialFulfillmentItemContentToJSON,
  type BatchEntryExtended,
  type JSONBatchEntriesResponse,
  type JSONBatchEntryExtended,
  type ShipmentExtras,
} from '~/types/OrderBatchEntry';
import * as ordersBatch from '~/types/OrdersBatch';
import * as parcel from '~/types/Parcel';
import { ShipmentStatus } from '~/types/Shipment';
import { TransactionStatus, type Transaction } from '~/types/Transaction';
import { trackingHelper } from '~/utils/TrackingHelper';
import {
  RATES_UPDATED_EVENT,
  RETURN_BATCH_ENTRY_CREATION_EVENT,
} from '~/utils/TrackingHelper/types';
import { trackDownload, trackWithGlobalProps } from '~/utils/TrackingHelper/utils';
import { parseBusinessRules } from '~/utils/businessRules';
import { FeatureFlags, decideFeatureFlag } from '~/utils/featureflags';
import { getPathValue } from '~/utils/getPathValue';
import { newRelic } from '~/utils/newRelic/newRelic';
import { observability } from '~/utils/observability';
import { batchAction } from '~/utils/redux/batch';
import { Events } from '~/utils/segment';
import { unknownToString } from '~/utils/utils';

import { selectState } from '..';
import {
  batchEntryPollPerformanceEnd,
  fetchOrdersPerformanceEnd,
  fetchOrdersPerformanceStart,
  pollPerformanceStart,
  purchaseableOrdersPerformanceEnd,
  shipmentPollPerformanceEnd,
} from './orders-performance.util';
import { synchronizeOrdersV2, synchronizeRateRefreshToOrdersV2 } from './orders-v2-sync.util';

export const BATCH_ENTRIES_POLL_TIMEOUT = 1 * 60 * 1000;

const transformFilters = (filters: Record<string, unknown>, orderStatus: string) => {
  if (!filters) return {};

  const { tag, ...rest } = filters;

  return {
    ...rest,
    order_status:
      orderStatus === 'pending'
        ? [
            order.OrderStatus.PAID,
            order.OrderStatus.UNKNOWN,
            order.OrderStatus.AWAITPAY,
            order.OrderStatus.PARTIALLY_FULFILLED,
          ]
        : orderStatus === 'fulfilled'
        ? [order.OrderStatus.SHIPPED]
        : orderStatus === 'cancelled'
        ? [order.OrderStatus.CANCELLED]
        : [],
    tag_id: tag,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOrderSearch = (filters: any) =>
  filters['order_status'].length > 0 || Object.keys(filters).length > 1;

export function* fetchOrder(action: ReturnType<typeof ordersActions.fetchOrder>) {
  try {
    const orderId = action.orderId;
    const response: AxiosResponse<order.JSONOrder> = yield call(
      ordersService.getWithReturns,
      orderId,
    );
    const payload: ordersActions.FetchOrderPayload = {
      order: order.fromJSON(response.data),
    };
    yield put(ordersActions.fetchOrderSuccess(payload));
  } catch (err) {
    yield put(ordersActions.fetchOrderError());
  }
}

export function* fetchOrders(action: ReturnType<typeof ordersActions.fetchOrders>) {
  try {
    fetchOrdersPerformanceStart();

    const { filters } = action.payload;
    const transformedFilters = transformFilters(filters, action.payload.orderStatus);

    const response: AxiosResponse<{
      count: number;
      results: order.JSONOrder[];
    }> = yield call(
      ordersService.getAll,
      action.payload.page,
      transformedFilters,
      action.payload.fetchCount,
      action.payload.showOnlyHidden,
      action.payload.sort,
      action.payload.sortBy,
      action.payload.orderBy,
    );

    const parsedOrders = response.data.results.map(order.fromJSON);
    const totalOrderCount = response.data.count;

    if (isOrderSearch(transformedFilters)) {
      yield call(trackWithGlobalProps, action.payload.appLocation, Events.order_search_completed, {
        filters: transformedFilters,
        orderCount: totalOrderCount,
      });
    }

    yield put(
      ordersActions.fetchOrdersSuccess(
        parsedOrders,
        response.data.count,
        action.payload.showOnlyHidden,
      ),
    );
    fetchOrdersPerformanceEnd({ orderCount: response.data.results.length, version: 'ORDERS_V1' });
  } catch (error) {
    newRelic.noticeError('Failed to fetch orders', { error });
    yield put(ordersActions.fetchOrdersError());
  }
}

export function* fetchOrderTags(action: ReturnType<typeof ordersActions.fetchOrdersSuccess>) {
  try {
    yield put(ordersActions.fetchOrderTags());

    const ordersData = yield selectState(getOrders);
    const orderIds = ordersData.map(({ objectId }) => objectId);

    if (orderIds.length === 0) {
      yield put(ordersActions.fetchOrderTagsError());

      return;
    }

    const response: AxiosResponse<{ results: order.WebOrder[] }> = yield call(getWebOrders, {
      hidden: action.payload.hidden,
      id: orderIds,
    });
    const ordersWithTags: order.OrderTagsMapping = response.data?.results?.reduce(
      (orderTags, order) => ({ ...orderTags, [order.object_id]: order.tags || [] }),
      {},
    );

    if (!ordersWithTags || Object.keys(ordersWithTags).length === 0)
      throw new Error('No tags found for orders');

    yield put(ordersActions.updateOrderTagsSuccess(ordersWithTags));
    yield put(ordersActions.fetchOrderTagsSuccess());
  } catch (error) {
    newRelic.noticeError('Failed to fetch order tags', { error });
    yield put(ordersActions.fetchOrderTagsError());
  }
}

export function* countOrders() {
  try {
    const ordersV2TableFlag = decideFeatureFlag(FeatureFlags.ORDERS_TABLE_API_V2_UI);

    let ordersCount = 0;
    let newLastOrderId: string | undefined;
    if (ordersV2TableFlag?.enabled) {
      const response: OrdersV2Response = yield call(fetchOrdersV2, { results: 1 });
      ordersCount = response.count;
      newLastOrderId = response.results[0]?.object_id;
    } else {
      const response: AxiosResponse<{ count: number; results: order.JSONOrder[] }> = yield call(
        ordersService.countOrders,
      );
      ordersCount = response.data.count;
      newLastOrderId = response.data.results[0]?.object_id;
    }

    const lastOrderId: string = yield selectState<string>(getLastOrderId);

    if (ordersCount > 0) {
      if (!lastOrderId) yield put(ordersActions.saveLastOrderId(newLastOrderId));
      else if (lastOrderId !== newLastOrderId)
        yield put(ordersActions.newOrdersFound(newLastOrderId));
    }

    yield put(ordersActions.countOrderSuccess(ordersCount));
  } catch {
    yield put(ordersActions.countOrderError());
  }
}

export function* syncOrders(action: ReturnType<typeof ordersActions.syncOrders>) {
  const userFirstStoreConnected: boolean = yield selectState<boolean>(hasStoreConnected);
  const totalOrdersInStore: number = yield selectState<number>(getTotalOrders);
  try {
    yield call(ordersService.syncOrders);
    yield call(countOrders);
    yield put(ordersActions.syncOrdersSuccess());

    if (userFirstStoreConnected && totalOrdersInStore === 0) {
      const queryParams = yield selectState(getParsedQS);
      const { hidden, order_by, order_status, page, results, sort, sort_by, ...filters } =
        queryParams;

      yield put(
        ordersActions.fetchOrders(
          action.payload.appLocation,
          page || 1,
          filters,
          results || '25',
          hidden || false,
          order_status || '',
          sort || '',
          sort_by || '_score',
          order_by || 'desc',
        ),
      );
    }
  } catch (err) {
    yield put(ordersActions.syncOrdersError());
  }
}

function* ordersPollWatcher() {
  while (true) {
    yield take(ActionKeys.ORDERS_POLL_START);

    yield race({
      cancel: take(ActionKeys.ORDERS_NEW_ORDERS_FOUND),
      task: call(ordersPollWorker),
    });
  }
}

function* ordersPollWorker() {
  while (true) {
    const POLL_WAIT_TIME = 1000 * 60 * 5; // 5min
    yield call(countOrders);
    yield delay(POLL_WAIT_TIME);
  }
}

export function* getOrderPackingSlip(action: ReturnType<typeof ordersActions.getPackingSlips>) {
  try {
    const { batchEntryIds, orderObjectIds } = order.actionableOrderSelectionIds(
      action.payload.orderSelection,
    );

    const response: AxiosResponse<{ slip_url: string }> = yield call(
      ordersService.getOrderPackingSlip,
      orderObjectIds,
      batchEntryIds,
    );
    action.payload.windowRef.location.assign(response.data.slip_url);
    yield call(
      trackDownload,
      action.payload.appLocation,
      'packing_slip',
      action.payload.orderSelection,
    );
  } catch (err) {
    yield put(ordersActions.getOrderPackingSlipError());
  }
}

// Given input of order IDs and batch entry IDs, retrieve transaction IDs
// filtered by the given batch entry IDs (for split order support)
export function* checkoutSendEmailNotification(
  action: ReturnType<typeof ordersActions.checkoutSendEmailNotifications>,
) {
  // support sending emails for batch entries. batch entry has transaction ID
  const transactionsToEmail: string[] = yield select(getOrderSuccessfulTransactionIds, {
    orderObjectIds: action.payload.orderObjectIds,
  });

  if (!transactionsToEmail) {
    yield put(
      ordersActions.checkoutSendEmailNotificationsError(
        'We hit an unexpected problem, please try again.',
      ),
    );
    return;
  }

  const batchEntries: BatchEntryExtended[] = yield select(getBatchEntriesByIds, {
    batchEntryIds: action.payload.batchEntryIds,
  });

  // Group batch entries by order
  const orderBatchEntries = batchEntries.reduce(
    (acc: Record<string, BatchEntryExtended[]>, batchEntry: BatchEntryExtended) => {
      const orderId = batchEntry.orderObjectId;
      const orderBatchEntries = acc[orderId] || [];
      return {
        ...acc,
        [orderId]: [...orderBatchEntries, batchEntry],
      };
    },
    {},
  );

  // curry'd function to select pick batch entry
  //    - If the order has outgoing and return only pick outgoing batch entries
  //    - If the order has only outgoing pick all
  //    - If the order has only return pick all
  const pickTransactionIDs = (orderId: string): BatchEntryExtended[] => {
    const batchEntries = orderBatchEntries[orderId];
    const outgoingBatchEntries = batchEntries.filter((batchEntry) => batchEntry.isReturn === false);
    const returnBatchEntries = batchEntries.filter((batchEntry) => batchEntry.isReturn === true);
    if (outgoingBatchEntries.length > 0 && returnBatchEntries.length > 0)
      return outgoingBatchEntries;

    return batchEntries;
  };

  // Reduce the grouped batch entries to an array of transactionIds using the pickTransactionIDs function above
  const batchEntryTransactionIds = Object.keys(orderBatchEntries).reduce(
    (acc: Maybe<string>[], orderId: string) => {
      const txnIds = pickTransactionIDs(orderId).map(
        (batchEntry) => batchEntry.transactionObjectId,
      );
      return [...acc, ...txnIds];
    },
    [],
  );

  const batchEntryTransactions = transactionsToEmail.filter((transactionId) =>
    batchEntryTransactionIds.includes(transactionId),
  );

  try {
    yield call(emailNotify, batchEntryTransactions);
    yield put(ordersActions.checkoutSendEmailNotificationsSuccess());
  } catch (err) {
    // TODO: Proper error
    yield put(
      ordersActions.checkoutSendEmailNotificationsError(
        'We hit an unexpected problem, please try again.',
      ),
    );
  }
}

function* hideOrder(action: ReturnType<typeof ordersActions.hideOrders>) {
  try {
    yield call(ordersService.hideOrder, action.payload.ids);
    yield put(ordersActions.hideOrderSuccess());
  } catch (err) {
    yield put(ordersActions.hideOrderError(UNEXPECTED_ERROR));
  }
}

function* patchBatchEntries(action: ReturnType<typeof ordersActions.patchBatchEntries>) {
  try {
    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    if (!action.payload.parcel && !action.payload.extras) {
      yield put(
        ordersActions.patchBatchEntriesError('orders:errors:patchBatchEntry:zeroOptionalArgs'),
      );
      return;
    }

    const patchResult: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.patchBatchEntries,
      batchEntriesIdsToUpdate,
      action.payload.parcel,
      action.payload.extras,
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: patchResult.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const batchEntries = patchResult.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.patchBatchEntriesSuccess(batchEntries));

    if (action.payload.parcel && !action.payload.isAutoSave) yield put(reset(PARCEL_FORM_NAME));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);
    const ordersMap: HashMap<string, Order> =
      yield selectState<HashMap<string, Order>>(getOrdersData);

    const ordersWithInvalidOrProcessingBatchEntryStatus = batchEntries
      .filter((batchEntry: BatchEntryExtended) =>
        [
          ...(action.payload.parcel ? [ordersBatch.BatchEntryStatus.PROCESSING_SHIPMENT] : []),
          ordersBatch.BatchEntryStatus.INVALID_ORDER,
        ].includes(batchEntry.objectStatus),
      )
      .map((batchEntry) => batchEntry.orderObjectId);

    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(
      ordersWithInvalidOrProcessingBatchEntryStatus.concat(ordersWithoutSenderAddress),
    );
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) yield put(ordersActions.fetchOrder(orderId));

    let responseOrderObjectIds: string[];

    if (action.payload.parcel) {
      // Ensure order has a toAddress, whether it's set in the order or in one of its batch entries
      const orderHasAddressToMap = batchEntries.reduce<Record<string, boolean>>(
        (map, batchEntry) => {
          map[batchEntry.orderObjectId] = !!batchEntry.addressTo;
          return map;
        },
        {},
      );

      responseOrderObjectIds = batchEntryOrderIds.filter(
        (orderId) => !!ordersMap[orderId].toAddress || orderHasAddressToMap[orderId],
      );
    } else {
      // Poll only those orders which have a package
      responseOrderObjectIds = batchEntryOrderIds.filter((orderId) =>
        isOrderShipmentRateable(ordersMap[orderId]),
      );
    }

    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));
  } catch (err) {
    yield put(ordersActions.patchBatchEntriesError(err));
  }
}

export function* updateParcel(action: ReturnType<typeof ordersActions.updateOrderParcel>) {
  try {
    yield put(startSubmit(PARCEL_FORM_NAME));
    const jsonParcel: parcel.SavedJSONParcel = parcel.toJSON(action.payload.parcel);

    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    const updatedBatchEntriesResult: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.updateParcel,
      batchEntriesIdsToUpdate,
      jsonParcel,
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: updatedBatchEntriesResult.data.map(
        (batchEntry) => batchEntry.order_object_id,
      ),
    });

    const batchEntries = updatedBatchEntriesResult.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.updateOrderParcelSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);
    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const ordersWithInvalidOrProcessingBatchEntryStatus = batchEntries
      .filter((batchEntry: BatchEntryExtended) =>
        [
          ordersBatch.BatchEntryStatus.INVALID_ORDER,
          ordersBatch.BatchEntryStatus.PROCESSING_SHIPMENT,
        ].includes(batchEntry.objectStatus),
      )
      .map((batchEntry) => batchEntry.orderObjectId);

    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(
      ordersWithInvalidOrProcessingBatchEntryStatus.concat(ordersWithoutSenderAddress),
    );

    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) yield put(ordersActions.fetchOrder(orderId));

    // Ensure order has a toAddress, whether it's set in the order or in one of its batch entries
    const orderHasAddressToMap = batchEntries.reduce<Record<string, boolean>>((map, batchEntry) => {
      map[batchEntry.orderObjectId] = !!batchEntry.addressTo;
      return map;
    }, {});

    const responseOrderObjectIds = batchEntryOrderIds.filter(
      (orderId) => !!ordersMap[orderId].toAddress || orderHasAddressToMap[orderId],
    );

    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));

    // Using setSubmitFailed because that actually clears the value
    // of submitting, setSubmitSuccess does not.
    yield put(setSubmitFailed(PARCEL_FORM_NAME));
  } catch (error) {
    newRelic.noticeError('Failed to update parcel', { error });

    yield put(ordersActions.updateOrderParcelError(error));

    const batchEntries: BatchEntryExtended[] = yield select(getBatchEntriesByOrderObjectIds, {
      orderObjectIds: Object.keys(action.payload.orderSelection),
    });

    const shipmentIds = batchEntries
      .map((batchEntry) => batchEntry.shipmentObjectId)
      .filter((shipmentId): shipmentId is string => shipmentId !== null);

    yield put(clearRates(shipmentIds));
  }
}

export function* updateBatchEntriesExtras(
  action: ReturnType<typeof ordersActions.updateBatchEntriesExtras>,
) {
  try {
    yield put(startSubmit(action.payload.form));

    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.updateBatchEntryExtras,
      batchEntriesIdsToUpdate,
      action.payload.extras,
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    // Using setSubmitFailed because that actually clears the value
    // of submitting, setSubmitSuccess does not.
    yield put(setSubmitFailed(action.payload.form));

    const batchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.updateBatchEntriesExtrasSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);

    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const batchEntriesWithInvalidOrder = batchEntries
      .filter(
        (batchEntry: BatchEntryExtended) =>
          batchEntry.objectStatus === ordersBatch.BatchEntryStatus.INVALID_ORDER,
      )
      .map((batchEntry) => batchEntry.orderObjectId);
    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(batchEntriesWithInvalidOrder.concat(ordersWithoutSenderAddress));
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) yield put(ordersActions.fetchOrder(orderId));

    // Poll only those orders which have a package
    const responseOrderObjectIds = batchEntryOrderIds.filter((orderId) =>
      isOrderShipmentRateable(ordersMap[orderId]),
    );
    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));
  } catch (err) {
    yield put(setSubmitFailed(action.payload.form));
  }
}

function* patchUpdateBatchEntriesExtras(
  action: ReturnType<typeof ordersActions.patchUpdateBatchEntriesExtras>,
) {
  try {
    const { batchEntriesIdsToUpdate } = yield call(
      getBatchEntriesIdsToUpdate,
      action.payload.orderSelection,
    );

    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.patchUpdateBatchEntryExtras,
      batchEntriesIdsToUpdate,
      action.payload.extras,
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const batchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.patchUpdateBatchEntriesExtrasSuccess(batchEntries));

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);

    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const batchEntriesWithInvalidOrder = batchEntries
      .filter(
        (batchEntry: BatchEntryExtended) =>
          batchEntry.objectStatus === ordersBatch.BatchEntryStatus.INVALID_ORDER,
      )
      .map((batchEntry) => batchEntry.orderObjectId);

    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId].addressFrom,
    );

    const ordersToFetch = uniq(batchEntriesWithInvalidOrder.concat(ordersWithoutSenderAddress));
    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) yield put(ordersActions.fetchOrder(orderId));

    // Poll only those orders which have a package
    const responseOrderObjectIds = batchEntryOrderIds.filter((orderId) =>
      isOrderShipmentRateable(ordersMap[orderId]),
    );
    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));
    // In case we want to run any function from the action dispatcher
    if (action.payload.callback) action.payload.callback();
  } catch (err) {
    yield put(ordersActions.patchUpdateBatchEntriesExtrasError(unknownToString(err)));
  }
}

export function* patchUpdateBatchEntriesExtrasPackingSlipNote(
  action: ReturnType<typeof ordersActions.patchUpdateBatchEntriesExtrasPackingSlipNote>,
) {
  try {
    let batchEntryIds: number[] = action.payload.batchEntryIds || [];
    //Create batch entries if the orders does not have batch entries (no batch entry ids passed)
    if (!action.payload.batchEntryIds) {
      const { batchEntriesIdsToUpdate } = yield call(
        getBatchEntriesIdsToUpdate,
        action.payload.orderSelection,
      );
      batchEntryIds = batchEntriesIdsToUpdate;
    }
    const extras: Partial<ShipmentExtras> = {
      packingSlipText: action.payload.packingSlipText,
    };
    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.patchUpdateBatchEntryExtras,
      batchEntryIds,
      extras,
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const batchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.patchUpdateBatchEntriesExtrasSuccess(batchEntries));
    yield put(
      addSnackbarMessage({
        i18n: true,
        message: 'orders:drawers.packingSlip.update.success',
        severity: 'success',
      }),
    );

    const batchEntryOrderIds = batchEntries.map((batchEntry) => batchEntry.orderObjectId);

    const ordersMap: HashMap<string, Order> = yield select(getOrdersData);

    const batchEntriesWithInvalidOrder = batchEntries
      .filter(
        (batchEntry: BatchEntryExtended) =>
          batchEntry.objectStatus === ordersBatch.BatchEntryStatus.INVALID_ORDER,
      )
      .map((batchEntry) => batchEntry.orderObjectId);

    if (!Object.values(ordersMap).length) return;

    const ordersWithoutSenderAddress = batchEntryOrderIds.filter(
      (orderId) => !ordersMap[orderId]?.addressFrom,
    );
    const ordersToFetch = uniq(batchEntriesWithInvalidOrder.concat(ordersWithoutSenderAddress));

    // Update orders in the store that don't have sender addresses or orders that have been marked as invalid
    for (const orderId of ordersToFetch) yield put(ordersActions.fetchOrder(orderId));

    // Poll only those orders which have a package
    const responseOrderObjectIds = batchEntryOrderIds.filter((orderId) => {
      const order = ordersMap[orderId];
      return !!order && isOrderShipmentRateable(order);
    });
    yield put(ordersActions.startBatchEntriesPolling(responseOrderObjectIds));
  } catch (err) {
    yield put(ordersActions.patchUpdateBatchEntriesExtrasError(unknownToString(err)));
    yield put(
      addSnackbarMessage({
        i18n: true,
        message: 'orders:drawers.packingSlip.update.error',
        severity: 'error',
      }),
    );
  }
}

export function* updateBatchEntriesAddress({
  payload: {
    address: originalAddress,
    addressBookAddress,
    addressType,
    combineSenderReturnAddresses,
    createUserAddress,
    orderSelection,
    source,
    trackingReference,
    updateUserAddress,
  },
}: ReturnType<typeof ordersActions.updateBatchEntriesAddress>) {
  const batchEntryCount = Object.values(orderSelection).reduce(
    (count, batchEntryIds) => count + (batchEntryIds.length || 1),
    0,
  );
  try {
    const { batchEntriesIdsToUpdate } = yield call(getBatchEntriesIdsToUpdate, orderSelection);

    const address = { ...originalAddress };

    if (
      [RETURN_ADDRESS_KIND, SENDER_ADDRESS_KIND].includes(addressType) &&
      !combineSenderReturnAddresses &&
      createUserAddress &&
      !!(address as APIAddress).id
    ) {
      // When updating a sender/return address AND NOT combining sender return addresses
      // Remove id from address object passed to editAPIAddress service to prevent both addresses
      // from being edited if the edge case where an order is currently using combined sender and return address
      delete (address as APIAddress).id;
    }

    const result: Awaited<ReturnType<typeof ordersService.updateBatchEntryAddress>> = yield call(
      ordersService.updateBatchEntryAddress,
      sanitizeAddress(address),
      addressType,
      uniq(batchEntriesIdsToUpdate),
    );

    if (typeof result?.data?.map !== 'function')
      throw new Error(`updateBatchEntryAddress got unexpected result: ${result.data}`);

    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    if (createUserAddress) {
      // Call this saga instead of dispatching the action to block subsequent yields
      // until the work in this saga is done
      // This prevents a race condition where the address book does not have the most up to date address
      // and a consumer attempts to compare selected address to the ones on address book
      yield call(
        addressSagas.createAPIAddress,
        addressActions.createAPIAddress({
          address,
          trackingReference,
        }),
      );
    } else if (updateUserAddress && addressBookAddress) {
      // Call this saga instead of dispatching the action to block subsequent yields
      // until the work in this saga is done
      // This prevents a race condition where the address book does not have the most up to date address
      // and a consumer attempts to compare selected address to the ones on address book
      yield call(
        addressSagas.editAPIAddress,
        addressActions.editAPIAddress({
          address: addressBookAddress,
          id: addressBookAddress.id,
        }),
      );
    }

    const parsedBatchEntries = result.data
      .map(batchEntryExtendedFromJSON)
      .filter((batchEntry) => !batchEntry.transactionObjectId);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));

    // Start polling the batch entries

    yield put(
      ordersActions.startBatchEntriesPolling(
        parsedBatchEntries.map((batchEntry) => batchEntry.orderObjectId),
      ),
    );

    yield put(
      ordersActions.updateBatchEntriesAddressSuccess(
        batchEntryCount,
        `orders:success:batch:${addressType}`,
        parsedBatchEntries,
        source,
      ),
    );
  } catch (err) {
    yield put(
      ordersActions.updateBatchEntriesAddressError(
        batchEntryCount,
        `orders:errors:batch:${addressType}`,
        source,
      ),
    );
  }
}

export function* updateBatchEntriesServiceLevelToken(
  action: ReturnType<typeof ordersActions.updateBatchEntriesServiceLevelToken>,
) {
  try {
    const { accountObjectId, orderSelection, preferredServiceLevelToken } = action.payload;

    // don't call getBatchEntriesIdsToUpdate here.  That POSTs to the backend to get existing or
    // create new batch entries for the order as appropriate; but it also resets the batch entries
    // to PENDING status and clears their rates, which we don't want to happen when we're just
    // updating the service level token.
    //
    // This may need to be updated when we start allowing users to select service levels before
    // rates are available, if there are then cases where a user can select a service level for
    // an order that doesn't have a batch entry at all yet.
    const batchEntryIdsToUpdate = compact(flatten(Object.values(orderSelection))).map((idString) =>
      Number(idString),
    );

    const result: JSONBatchEntriesResponse = yield call(
      ordersService.updateBatchEntryServiceLevelToken,
      preferredServiceLevelToken,
      batchEntryIdsToUpdate,
      accountObjectId,
    );

    const parsedBatchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.updateBatchEntriesServiceLevelTokenSuccess(parsedBatchEntries));
  } catch (err) {
    yield put(ordersActions.updateBatchEntriesServiceLevelTokenError());
  }
}

export function* updateBatchEntriesShipmentDate(
  action: ReturnType<typeof ordersActions.updateBatchEntriesShipmentDate>,
) {
  let orderObjectIds;

  try {
    const { orderSelection, shipmentDate } = action.payload;
    orderObjectIds = Object.keys(orderSelection);

    const { batchEntriesIdsToUpdate } = yield call(getBatchEntriesIdsToUpdate, orderSelection);

    // optimistically update the shipment date (so that the new date displays in the actions bar)
    yield put(
      ordersActions.updateBatchEntriesShipmentDateInitiated(batchEntriesIdsToUpdate, shipmentDate),
    );

    const result: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.updateBatchEntryShipmentDate,
      shipmentDate,
      uniq(batchEntriesIdsToUpdate),
    );
    yield call(synchronizeOrdersV2, {
      orderObjectIds: result.data.map((batchEntry) => batchEntry.order_object_id),
    });

    const parsedBatchEntries = result.data.map(batchEntryExtendedFromJSON);
    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(ordersActions.updateBatchEntriesShipmentDateSuccess());
  } catch (err) {
    yield put(ordersActions.updateBatchEntriesShipmentDateError());
  } finally {
    // Start polling the batch entries regardless of whether the update succeeded or failed (in the latter case,
    // we need to revert the optimistically-updated shipment date)
    if (orderObjectIds) yield put(ordersActions.startBatchEntriesPolling(orderObjectIds));
  }
}

export function* fetchOrderBatchEntry(
  action: ReturnType<typeof ordersActions.fetchOrderBatchEntry>,
) {
  try {
    const response: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.getOrderBatchEntry,
      action.payload.orderObjectId,
      true,
    );
    const batchEntriesExtended = response.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(batchEntriesExtended));
  } catch (err) {
    yield put(ordersActions.fetchOrderBatchEntryError(action.payload.orderObjectId));
  }
}

// OrdersV2 drawer (in its current iteration) needs to fetch OrderV1 and BatchEntry
export function* loadAndSelectOrders(action: ReturnType<typeof ordersActions.loadAndSelectOrders>) {
  try {
    const { orderSelection, rowModelBeingEdited } = action.payload;

    yield call(loadMissingOrdersData, orderSelection);

    yield put(ordersActions.clearSelectedOrders(true));
    yield put(ordersActions.selectOrder(orderSelection, true));

    if (rowModelBeingEdited && rowModelBeingEdited.length > 0)
      yield put(ordersActions.updateRowModelBeingEdited(rowModelBeingEdited));
  } catch (error) {
    yield put(ordersActions.fetchOrderError());
    yield put(ordersActions.stopBatchEntriesPolling());
  }
}

function* loadDataForSelectedOrders(action: ReturnType<typeof ordersActions.selectOrder>) {
  yield call(loadMissingOrdersData, action.payload);
}

function* loadMissingOrdersData(orderSelection: order.OrderSelection) {
  try {
    const ordersData: Record<string, Order> = yield select(getOrdersData);
    const batchEntries: Record<string, BatchEntryExtended[]> = yield select(getBatchEntryData);
    const missingOrderObjectIds = Object.entries(orderSelection)
      .filter(([orderId, batchEntryIds]) => {
        if (!ordersData[orderId]) return true;
        if (batchEntryIds.some((batchEntryId) => !batchEntries[batchEntryId])) return true;
        return false;
      })
      .map(([orderId]) => orderId);

    if (missingOrderObjectIds.length === 0) return false; // nothing to load

    for (const orderObjectId of missingOrderObjectIds)
      yield put(ordersActions.fetchOrder(orderObjectId));
    yield put(ordersActions.startBatchEntriesPolling(Object.keys(orderSelection)));

    return true; // data loading started
  } catch (error) {
    yield put(ordersActions.fetchOrderError());
    yield put(ordersActions.stopBatchEntriesPolling());
  }
}

function* waitForMissingOrdersData() {
  yield all(
    [ActionKeys.ORDER_FETCH_SUCCESS, ActionKeys.ORDERS_BATCH_ENTRIES_POLL_END].map((type) =>
      take(type),
    ),
  );
}

// Batch entries polling logic
// Worker will be stored here to prevent starting many
// and also being able to kill it once it completed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let worker: ForkEffect<SagaReturnType<any>> | null = null;
// Keep track of the order and the unix time when the poll started for it.
// So far that unix timestamp has no use, if we implement timeout messaging per
// order it will come in handy.
let ordersToPoll: { [key: string]: number } = {};
// Keep track of the shipment and the unix time when the poll started for it.
// So far that unix timestamp has no use, if we implement timeout messaging per
// order it will come in handy.
const shipmentsToPoll: { [key: string]: number } = {};

/**
 * This is a helper generator function to get all the batch entries for the orders
 * This would create new batch entry if it's missing for an order
 * @param orderObjectIDs
 * @returns
 */
export function* getBatchEntries(orderObjectIDs: string[]) {
  let batchEntries: BatchEntryExtended[] = [];
  let missingOrders: string[] = [];
  // Keep fetching batch entries until we get batch entry for
  // all the orders we are polling
  // We create a batch entry for each order
  // Issue resolved: https://shippo.atlassian.net/browse/ENG-41017
  try {
    const response: AxiosResponse<{ results: JSONBatchEntryExtended[] }> = yield call(
      ordersService.getOrderBatchEntries,
      {
        includeReturns: true,
        orderObjectIds: missingOrders.length ? missingOrders : orderObjectIDs,
      },
    );
    batchEntries = [...response.data.results.map(batchEntryExtendedFromJSON)];
    const orderBatchEntriesMap = batchEntries.reduce<{
      [orderObjectId: string]: BatchEntryExtended[];
    }>((map, be) => {
      if (map[be.orderObjectId]) map[be.orderObjectId].push(be);
      else map[be.orderObjectId] = [be];

      return map;
    }, {});

    missingOrders = orderObjectIDs.filter((objectId) => !orderBatchEntriesMap[objectId]);
    // We create batch entries for the orders with missing batch entry
    if (missingOrders.length > 0) {
      const response: AxiosResponse<{ results: JSONBatchEntryExtended[] }> = yield call(
        ordersService.getOrCloneBatchEntriesForOrders,
        missingOrders,
        [],
      );
      batchEntries = [...batchEntries, ...response.data.results.map(batchEntryExtendedFromJSON)];
    }
  } catch (err) {
    yield put(
      ordersActions.fetchOrderBatchEntriesError(missingOrders ? missingOrders : orderObjectIDs),
    );
  }

  return batchEntries;
}

export function* getSelectedBatchEntryIds() {
  const pathName = yield select(getPathname);
  const batchEntryIdFromUrl = getPathValue(pathName, 'shipments');

  if (batchEntryIdFromUrl) return [Number(batchEntryIdFromUrl)];

  const selectedOrders: order.OrderSelection = yield select(getSelectedOrders);

  return flatten(Object.values(selectedOrders));
}

export function* batchEntriesWorker(
  ordersToPoll: { [key: string]: number },
  shipmentsToPoll: { [key: string]: number },
) {
  const orderCount = Object.keys(ordersToPoll).length;
  const originalOrderObjectIds = Object.keys(ordersToPoll);

  while (true) {
    try {
      const orderObjectIds = Object.keys(ordersToPoll);
      if (orderObjectIds.length > 0) {
        yield put(ordersActions.fetchOrderBatchEntries(orderObjectIds));

        const completedBatchEntries: BatchEntryExtended[] = [];

        const batchEntries: BatchEntryExtended[] = yield call(getBatchEntries, orderObjectIds);

        const batchEntriesByOrderId = batchEntries.reduce(
          (
            map: {
              [orderObjectId: string]: number[];
            },
            batch,
          ) => {
            if (!map[batch.orderObjectId]) map[batch.orderObjectId] = [];

            map[batch.orderObjectId].push(batch.id);

            return map;
          },
          {},
        );

        batchEntries.forEach((batch) => {
          if (
            [
              ordersBatch.BatchEntryStatus.COMPLETE_TRANSACTION,
              ordersBatch.BatchEntryStatus.INVALID_ORDER,
              ordersBatch.BatchEntryStatus.PENDING,
              ordersBatch.BatchEntryStatus.RATE_NOT_AVAILABLE,
              ordersBatch.BatchEntryStatus.SHIPMENT_REQUEST_FAILED,
              ordersBatch.BatchEntryStatus.TRANSACTION_REQUEST_FAILED,
            ].includes(batch.objectStatus)
          ) {
            batchEntriesByOrderId[batch.orderObjectId] = batchEntriesByOrderId[
              batch.orderObjectId
            ].filter((id) => id !== batch.id);

            completedBatchEntries.push(batch);
          }

          if (
            batch.shipmentObjectId &&
            batch.objectStatus === ordersBatch.BatchEntryStatus.COMPLETE_SHIPMENT
          ) {
            shipmentsToPoll[batch.shipmentObjectId] = new Date().getTime();

            batchEntriesByOrderId[batch.orderObjectId] = batchEntriesByOrderId[
              batch.orderObjectId
            ].filter((id) => id !== batch.id);

            completedBatchEntries.push(batch);
          }

          if (!batchEntriesByOrderId[batch.orderObjectId].length)
            delete ordersToPoll[batch.orderObjectId];
        });
        yield put(ordersActions.fetchOrderBatchEntriesSuccess(completedBatchEntries));

        if (Object.keys(ordersToPoll).length === 0) batchEntryPollPerformanceEnd({ orderCount });
      }

      const shipmentObjectIds = Object.keys(shipmentsToPoll);
      if (shipmentObjectIds.length > 0) {
        const shipmentsData: { [shipmentId: string]: Maybe<Shipment> } =
          yield select(getShipmentsData);

        for (const shipmentObjectId of shipmentObjectIds) {
          const trackingReference = trackingHelper.getTrackingReference(
            RATES_UPDATED_EVENT.name,
            'other',
            {
              rateIds: undefined,
            },
          );
          const shipmentIsLoading: boolean = yield select(getShipmentIsLoading, {
            shipmentId: shipmentObjectId,
          });

          if (shipmentIsLoading) continue;

          const shipment = shipmentsData[shipmentObjectId];

          if (!shipment) {
            yield put(batchAction(fetchShipment(shipmentObjectId, trackingReference)));
            shipmentsToPoll[shipmentObjectId] = new Date().getTime();
            continue;
          }

          if ([ShipmentStatus.ERROR, ShipmentStatus.SUCCESS].includes(shipment.status))
            delete shipmentsToPoll[shipment.objectId];
          else yield put(fetchShipment(shipmentObjectId, trackingReference));
        }

        if (Object.keys(shipmentsToPoll).length === 0) shipmentPollPerformanceEnd({ orderCount });
      }

      if (Object.keys(ordersToPoll).length === 0 && Object.keys(shipmentsToPoll).length === 0) {
        purchaseableOrdersPerformanceEnd({ orderCount, version: 'ORDERS_V1' });

        // Kick off the V2 sync in the background before ending the poll. Once
        // ORDERS_BATCH_ENTRIES_POLL_END is dispatched, batchEntriesPollRace will cancel this
        // worker, so code after `yield put(ordersActions.stopBatchEntriesPolling())` would not
        // reliably run.
        synchronizeOrdersV2({ orderObjectIds: originalOrderObjectIds }).catch((error) => {
          newRelic.noticeError('Failed to synchronize orders V2 after batch poll', { error });
        });
        yield put(ordersActions.stopBatchEntriesPolling());
      } else {
        // Prevent timeout
        if (ENV_ID === 'test') break;

        yield delay(750);
      }
    } catch (error) {
      newRelic.noticeError('Failed to poll', { error });

      // TODO: Set errors for batch entries that we were still polling for
      yield put(ordersActions.stopBatchEntriesPolling());
    }

    if (ENV_ID === 'test') break;
  }
}

function* batchEntriesPollRace() {
  // TODO: What is a better type for this?
  const result: { timeout: boolean } = yield race({
    cancel: take([ActionKeys.ORDERS_BATCH_ENTRIES_POLL_END]),
    task: call(batchEntriesWorker, ordersToPoll, shipmentsToPoll),
    timeout: delay(BATCH_ENTRIES_POLL_TIMEOUT, true),
  });

  if (result.timeout) {
    // Clear any remaining V1 loadState entries so rows don't stay in a loading state indefinitely.
    const remainingOrderIds = Object.keys(ordersToPoll);
    if (remainingOrderIds.length > 0)
      yield put(ordersActions.fetchOrderBatchEntriesError(remainingOrderIds));
    ordersToPoll = {};
    // TODO: shipmentsToPoll is not cleared here. Stale shipment IDs will persist into the next
    // polling session. Fix in a follow-up — requires determining what V2 state to clear on timeout.
  }

  worker = null;
}

function* batchEntriesPollWatcher() {
  while (true) {
    const action:
      | ReturnType<typeof ordersActions.fetchOrdersSuccess>
      | ReturnType<typeof ordersActions.startBatchEntriesPolling> = yield take([
      ActionKeys.ORDERS_BATCH_ENTRIES_POLL_START,
      ActionKeys.ORDERS_FETCH_SUCCESS,
    ]);

    pollPerformanceStart();

    let orderObjectIds: string[] = [];
    switch (action.type) {
      case ActionKeys.ORDERS_BATCH_ENTRIES_POLL_START:
        orderObjectIds = action.payload.orderObjectIds;
        break;

      case ActionKeys.ORDERS_FETCH_SUCCESS:
        orderObjectIds = action.payload.orders.map((order) => order.objectId);
        break;
    }

    for (const orderObjectId of orderObjectIds) ordersToPoll[orderObjectId] = new Date().getTime();

    if (!worker) worker = yield fork(batchEntriesPollRace);
  }
}

function* batchEntriesRegen(action: ReturnType<typeof ordersActions.createAnotherLabel>) {
  try {
    const orderSelection = action.payload.orderSelection;
    const orderObjectIDs: string[] = [];
    const batchEntryIDs: ordersBatch.BatchEntryId[] = [];
    const initialBatchEntries: BatchEntryExtended[] = yield call(
      orderSelectionToBatchEntries,
      orderSelection,
    );

    Object.entries(orderSelection).forEach(([orderObjectId, orderBatchEntryIDs]) => {
      orderObjectIDs.push(orderObjectId);
      if (orderBatchEntryIDs?.length > 0) batchEntryIDs.push(...orderBatchEntryIDs);
    });

    const results: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.getOrCloneBatchEntriesForOrders,
      orderObjectIDs,
      batchEntryIDs,
    );

    const parsedBatchEntries = results.data.map(batchEntryExtendedFromJSON);

    // make sure to reset split orders that were not included in the initial request but returned in the response
    const resetBatchEntries = findResetBatchEntries(initialBatchEntries, parsedBatchEntries);

    yield put(ordersActions.clearBatchEntries(initialBatchEntries, resetBatchEntries));

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));

    yield put(
      ordersActions.startBatchEntriesPolling(
        resetBatchEntries.map((batchEntry) => batchEntry.orderObjectId),
      ),
    );

    if (parsedBatchEntries.length === 1) {
      const location: LocationDescriptorObject =
        yield selectState<LocationDescriptorObject>(getLocation);
      yield put(
        push(
          `/orders/${parsedBatchEntries[0].orderObjectId}/shipments/${parsedBatchEntries[0].id}${location.search}`,
        ),
      );
    }
  } catch (err) {
    // todo: raise error in log
  }
}

export function* retryShipment(action: ReturnType<typeof ordersActions.retryShipment>) {
  try {
    const orderSelection = action.payload.orderSelection;
    const batchEntryIDs = Object.values(orderSelection).flat();

    const results: AxiosResponse<JSONBatchEntryExtended[]> = yield call(
      ordersService.refreshBatchEntry,
      batchEntryIDs,
    );
    yield call(synchronizeRateRefreshToOrdersV2, {
      orderShipmentIds: batchEntryIDs,
    });

    const parsedBatchEntries = results.data.map(batchEntryExtendedFromJSON);

    yield put(ordersActions.fetchOrderBatchEntriesSuccess(parsedBatchEntries));
    yield put(
      ordersActions.startBatchEntriesPolling(
        parsedBatchEntries.map((batchEntry) => batchEntry.orderObjectId),
      ),
    );
    if (parsedBatchEntries.length === 1 && batchEntryIDs[0] !== parsedBatchEntries[0].id) {
      const location: LocationDescriptorObject =
        yield selectState<LocationDescriptorObject>(getLocation);
      yield put(
        push(
          `/orders/${parsedBatchEntries[0].orderObjectId}/shipments/${parsedBatchEntries[0].id}${location.search}`,
        ),
      );
    }

    return parsedBatchEntries;
  } catch (err) {
    // todo: raise error in log
  }
}

// fetches applied rules for a batch entry given batch entry id
function* fetchBatchEntryRules(action: ReturnType<typeof ordersActions.fetchBatchEntryRules>) {
  try {
    const response: AxiosResponse<{ rule: JSONBusinessRule }[]> = yield call(
      ordersService.fetchBatchEntryRules,
      action.payload.batchEntryId,
    );
    if (response.data.length === 0) {
      yield put(ordersActions.clearBatchEntryRules(action.payload.batchEntryId));
      return;
    }

    // for the /batch_entry/<id>/rule GET, returns an array of batch entry rule responses with rule nested
    const rule = parseBusinessRules(response.data.map((batchEntryRule) => batchEntryRule?.rule));

    yield put(
      ordersActions.fetchBatchEntryRulesSuccess(action.payload.batchEntryId, rule.businessRules),
    );
  } catch (err) {
    yield put(ordersActions.fetchBatchEntryRulesError(unknownToString(err)));
  }
}

export function* undoSplitOrder(action: ReturnType<typeof ordersActions.undoSplitOrder>) {
  try {
    const { orderObjectId } = action.payload;

    const ordersData: HashMap<string, Order> = yield select(getOrdersData);
    const orderData = ordersData[orderObjectId];

    if (!orderData) return;

    const originalShipments = [
      orderData.lineItems.map((lineItem) => ({
        lineItem: lineItem.objectId,
        quantity: lineItem.quantity || 0,
      })),
    ];

    const response: AxiosResponse<order.JSONOrder[]> = yield call(
      ordersService.createPartialFulfillmentRequests,
      orderObjectId,
      originalShipments.map((contents) => contents.map(partialFulfillmentItemContentToJSON)),
    );
    yield call(synchronizeOrdersV2, { orderObjectIds: [orderObjectId] });

    const orderWithPartialFulfillmentRequests = order.fromJSON(response.data[0]);
    yield put(
      ordersActions.createPartialFulfillmentRequestsSuccess({
        isUndo: true,
        order: orderWithPartialFulfillmentRequests,
      }),
    );

    yield put(
      addSnackbarMessage({
        i18n: true,
        i18nValues: { orderNumber: orderData.orderNumber },
        message: `orders:splitOrder:form:result:undo:success${
          orderData.orderNumber ? 'WithNumber' : ''
        }`,
        severity: 'success',
      }),
    );
  } catch (error) {
    yield put(
      addSnackbarMessage({
        i18n: true,
        message: 'orders:splitOrder:form:result:undo:error',
        severity: 'error',
      }),
    );
  }
}

export function* createPartialFulfillmentRequests(
  action: ReturnType<typeof ordersActions.createPartialFulfillmentRequests>,
) {
  const { orderObjectId, partialFulfillments } = action.payload;

  const ordersData: HashMap<string, Order> = yield select(getOrdersData);
  const orderData = ordersData[orderObjectId];

  const orderLineItemsCount = orderData?.lineItems?.length || 0;
  const orderLineItemsQuantity = (orderData?.lineItems || []).reduce(
    (acc, curr) => acc + curr.quantity,
    0,
  );

  try {
    const response: AxiosResponse<order.JSONOrder[]> = yield call(
      ordersService.createPartialFulfillmentRequests,
      orderObjectId,
      partialFulfillments.map((contents) => contents.map(partialFulfillmentItemContentToJSON)),
    );
    yield call(synchronizeOrdersV2, { orderObjectIds: [orderObjectId] });

    const orderWithPartialFulfillmentRequests = order.fromJSON(response.data[0]);
    yield put(
      ordersActions.createPartialFulfillmentRequestsSuccess({
        order: orderWithPartialFulfillmentRequests,
      }),
    );
    yield put(ordersActions.closeOrderSplitModal());

    const pathName: ReturnType<typeof getPathname> = yield select(getPathname);
    const batchEntryId = Number(pathName.slice(pathName.lastIndexOf('/') + 1, pathName.length));
    const batchEntry: ReturnType<typeof getBatchEntryById> = !isNaN(batchEntryId)
      ? yield select(getBatchEntryById, {
          batchEntryId,
        })
      : null;
    const isPreSplit = pathName === `/orders/${orderWithPartialFulfillmentRequests.objectId}`;
    const qs: ReturnType<typeof getQS> = yield select(getQS);

    if (batchEntry || isPreSplit) {
      // find current one by rank
      const currentWorkingBatchEntry =
        orderWithPartialFulfillmentRequests.partialFulfillmentRequests.find((be) => {
          if (!batchEntry) return true;

          const isSingleBatchEntryToSplit =
            batchEntry.partialFulfillmentRank === 0 && be.partialFulfillmentRank === 1;
          const isExistingSplit = be.partialFulfillmentRank === batchEntry.partialFulfillmentRank;
          const isDeletedSplit =
            batchEntry.partialFulfillmentRank >
            orderWithPartialFulfillmentRequests.partialFulfillmentRequests.length;

          if (isSingleBatchEntryToSplit || isExistingSplit || isDeletedSplit) return true;
        });

      if (currentWorkingBatchEntry) {
        yield put(
          replace(
            `/orders/${orderWithPartialFulfillmentRequests.objectId}/shipments/${currentWorkingBatchEntry.id}${qs}`,
          ),
        );
      } else {
        yield put(replace(`/orders/${orderWithPartialFulfillmentRequests.objectId}${qs}`));
      }
    }

    observability.successEvent('SPLIT_ORDER_DIALOG_SUBMIT', {
      lineItemCount: orderLineItemsCount,
      lineItemsQuantity: orderLineItemsQuantity,
      shipmentsCount: partialFulfillments.length,
    });

    const orderNumber = orderData?.orderNumber;

    yield put(
      addSnackbarMessage({
        i18n: true,
        i18nValues: { orderNumber },
        message: `orders:splitOrder:form:result:success${orderNumber ? 'WithNumber' : ''}`,
        severity: 'success',
      }),
    );
  } catch (err) {
    yield put(
      addSnackbarMessage({
        i18n: true,
        message: 'orders:splitOrder:form:result:error',
        severity: 'error',
      }),
    );

    observability.errorEvent('SPLIT_ORDER_DIALOG_SUBMIT', {
      errorMessage: err.message || 'Unknown',
      lineItemCount: orderLineItemsCount,
      lineItemsQuantity: orderLineItemsQuantity,
      shipmentsCount: partialFulfillments.length,
    });

    // setting a boolean for this for now but once we have non-generic
    // backend errors we want to surface to the client this should be updated
    yield put(
      ordersActions.createPartialFulfillmentRequestsError({
        error: 'network error',
        orderObjectId,
      }),
    );
  }
}

function* toggleCheckoutModal(orders: order.OrderSelection) {
  const preCheckoutQueue: PreCheckoutSteps[] = [];

  const hasValidBilling: boolean = yield selectState<boolean>(userHasValidBilling);
  const isBillingBlocked: boolean = yield selectState<boolean>(userIsBillingBlocked);
  const isHighFraudRisk: boolean = yield selectState<boolean>(userIsHighFraudRisk);
  const registrationStatuses: RegStatusByCarrierAccount = yield select(
    getIncompleteRegStatusFromRates,
    {
      batchEntryIds: flatten(Object.values(orders)),
    },
  );
  const carrierAccountsMap: { [objectId: string]: CarrierAccount } = yield select(
    getCarrierAccountsObjectIdMap,
  );
  const hasValidSenderAddress: boolean = yield selectState<boolean>(getIsUserAddressValid);
  const requiresEmailVerification: boolean = yield selectState<boolean>(
    getRequiresEmailVerification,
  );

  // Queue - order matters
  if (isBillingBlocked && isHighFraudRisk) {
    // Show generic contact customer support modal if user has blocked billing and is high risk
    preCheckoutQueue.push({ kind: 'account-alert' });
  }

  for (const [carrierAccountObjectId, regStatus] of Object.entries(registrationStatuses)) {
    const carrierAccount: CarrierAccount = carrierAccountsMap[carrierAccountObjectId];

    switch (carrierAccount.carrier.id) {
      case UPS.id:
        // Retrieve country from master account's metadata. Currently used to identify country of UPS master accounts.
        let accountCountry: CountryIso2 | undefined;
        if (carrierAccount.metadata && countries[carrierAccount.metadata as keyof typeof countries])
          accountCountry = countries[carrierAccount.metadata as keyof typeof countries].iso2;

        preCheckoutQueue.push({
          accountCountry,
          kind: 'ups-activation',
          // Show full UPS form if the incomplete reg status is not PENDING_TERMS (e.g. ERROR)
          showFullForm: !(regStatus === RegStatus.PENDING_TERMS && hasValidSenderAddress),
        });
        break;
      case CANADA_POST.id:
        preCheckoutQueue.push({
          kind: 'canada_post-activation',
          showFullForm: true,
        });
        break;
      case DHL_EXPRESS.id:
        preCheckoutQueue.push({
          kind: 'dhl_express-activation',
        });
        break;
      case ROYAL_MAIL_SF.id:
        preCheckoutQueue.push({
          kind: 'royal_mail_sf-activation',
        });
        break;
    }
  }

  if (!hasValidBilling) {
    if (requiresEmailVerification) preCheckoutQueue.push({ kind: 'email-verification' });

    preCheckoutQueue.push({ kind: 'credit-card-info' });
  }

  yield put(ordersActions.toggleCheckoutModal(orders, preCheckoutQueue));
}

export function* startCheckout(
  action: ReturnType<typeof ordersActions.startCheckout>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Generator<unknown, unknown, any> {
  try {
    const isIDVRequired: boolean = yield selectState<boolean>(getIsIDVRequired);

    if (!isIDVRequired) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return yield call(toggleCheckoutModal, action.payload.orders) as any;
    }

    const result: AxiosResponse<IDVerification.JSONIDVerification> =
      yield call(getVerificationStatus);
    const verification = IDVerification.fromJSON(result.data);

    switch (verification.state) {
      // User was previously in verification but now it has succeeded.
      case IDVerification.IDVerificationStatus.NO_VERIFICATION_NEEDED:
      case IDVerification.IDVerificationStatus.VERIFICATION_SUCCEEDED:
        // Calling refresh user saga directly. This will wait until refresh is done
        // and avoid that the selectors following this will work with stale data.
        yield call(refreshUser);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return yield call(toggleCheckoutModal, action.payload.orders) as any;

      default:
        // Dispatch action to set verification step
        yield put(setIdVerificationStatus(verification));
    }
  } catch (err) {
    // This is likely to be an error retrieving verification status. Handle that with a generic message.
    yield put(
      setIdVerificationStatus({
        errors: ['error_processing_request'],
        state: IDVerification.IDVerificationStatus.VERIFICATION_FAILED_NO_RETRY,
      }),
    );
  }
}

export function* updateExpiredShipments() {
  try {
    const orderSelection: order.OrderSelection = yield select(
      getUnpurchasedBatchEntriesOrderSelectionWithExpiredShipmentDate,
    );

    if (Object.keys(orderSelection).length)
      yield put(ordersActions.updateBatchEntriesShipmentDate(orderSelection, Date.now()));
  } catch (error) {
    newRelic.noticeError('Failed to update expired shipments', { error });
  }
}

export function* reStartCheckout() {
  try {
    // We are fetching all the transactions which were failed last time
    // so that we can re-run checkout on them once again.
    // The reason it's not passed as a prop because
    // the action dispatcher component should not be re-rendered based on the data
    const transactions: { [transactionId: string]: Readonly<Transaction> } = yield selectState<{
      [transactionId: string]: Readonly<Transaction>;
    }>(getTransactionsData);
    const selectedOrders: order.OrderSelection =
      yield selectState<order.OrderSelection>(getSelectedOrders);
    const transactionsForRerun = Object.values(transactions).filter(
      (transaction) =>
        transaction.status === TransactionStatus.ERROR &&
        transaction.order &&
        selectedOrders[transaction.order],
    );
    if (transactionsForRerun.length) {
      const orderSelection: order.OrderSelection = transactionsForRerun.reduce<{
        [key: string]: number[];
      }>((newOrders, transaction) => {
        if (transaction.order) newOrders[transaction.order] = selectedOrders[transaction.order];

        return newOrders;
      }, {});

      // This step is important. After updating the address it's possible
      // that the selected orders now have updated batch entry IDs
      // We need to derive new selected orders with new batch entry IDs
      const { newSelectedOrder } = yield call(getBatchEntriesIdsToUpdate, orderSelection);

      // We also need to update the order after bypassing an address,
      // otherwise order is still invalid, which keeps the
      // confirmation drawer in a loading state
      const refreshedBatchEntries: BatchEntryExtended[] | undefined = yield call(
        retryShipment,
        ordersActions.retryShipment(newSelectedOrder),
      );

      // Group the regenerated batch-entry ids per orderObjectId
      const refreshedSelection: order.OrderSelection = refreshedBatchEntries?.length
        ? refreshedBatchEntries.reduce<{ [orderObjectId: string]: number[] }>((accum, entry) => {
            (accum[entry.orderObjectId] ??= []).push(entry.id);
            return accum;
          }, {})
        : newSelectedOrder;

      // Sync the side-panel selection to the regenerated ids.
      yield put(ordersActions.syncSelectedOrders(refreshedSelection));

      // Restore rowModelBeingEdited so the orders-table rowsEffect can re-derive the correct selectedOrders instead of clearing it
      const rowModelBeingEdited = refreshedBatchEntries?.length
        ? refreshedBatchEntries.map((entry) =>
            buildOrderTableRowKey(entry.orderObjectId, entry.partialFulfillmentRank),
          )
        : Object.keys(refreshedSelection);
      yield put(ordersActions.updateRowModelBeingEdited(rowModelBeingEdited));

      /**
       * Start checkout with the regenerated ids so orderAction.orders tracks them too.
       * preserveRateAfterPoll keeps the pending rate-restoration entries alive across the immediate (loading-state) drawer open;
       * autoSelectRate re-selects each rate once the regenerated shipments' rates poll in.
       */
      yield put(ordersActions.startCheckout(refreshedSelection, true, true));
    }
  } catch (error) {
    newRelic.noticeError('Failed to restart checkout', { error });
  }
}

function* retryCheckout(action: ReturnType<typeof ordersActions.retryCheckout>) {
  const { orderSelection } = action.payload;

  try {
    const { newSelectedOrder } = yield call(getBatchEntriesIdsToUpdate, orderSelection);
    yield put(ordersActions.retryShipment(newSelectedOrder));
    yield put(ordersActions.startCheckout(newSelectedOrder, true));
  } catch (error) {
    newRelic.noticeError('Failed to retry checkout', { error });
  }
}

function* createReturnLabel(action: ReturnType<typeof ordersActions.createOrderReturnLabel>) {
  try {
    const { orderObjectId, shipment } = action.payload;

    const hasToWait = yield call(loadMissingOrdersData, { [orderObjectId]: [] });
    if (hasToWait) yield call(waitForMissingOrdersData);

    const parsedBatch = batchEntryExtendedFromJSON(shipment);
    parsedBatch.returnOfID = action.payload.returnOfID;
    const ordersData: Order[] = yield select(getOrdersData);
    const orderData = ordersData[orderObjectId];

    if (!orderData) throw new Error('Order data not found');

    const partialFulfillmentRequestsWithoutReturns =
      orderData.partialFulfillmentRequests.filter(({ isReturn }) => !isReturn) || [];

    const orderWithReturnLabel: Order = {
      ...orderData,
      partialFulfillmentRequests: [...partialFulfillmentRequestsWithoutReturns, parsedBatch],
    };
    const orderToBatchEntry: order.OrderToBatchEntry = {
      [orderObjectId]: parsedBatch.id,
    };

    yield put(
      ordersActions.createPartialFulfillmentRequestsSuccess({
        isReturn: true,
        order: orderWithReturnLabel,
      }),
    );
    yield put(ordersActions.fetchOrderBatchEntries([orderObjectId]));
    yield put(ordersActions.clearSelectedOrders(true));
    yield put(ordersActions.selectOrder({ [orderObjectId]: [parsedBatch.id] }, true));
    yield put(ordersActions.createOrderReturnLabelSuccess(orderToBatchEntry));

    if (shipment.parcels?.length) {
      const trackingReference = trackingHelper.getTrackingReference(
        RETURN_BATCH_ENTRY_CREATION_EVENT.name,
        'other',
        {},
      );

      yield put(
        updateBatchEntriesAddressAction({
          address: parsedBatch.addressFrom as Address,
          addressType: SENDER_ADDRESS_KIND,
          combineSenderReturnAddresses: false,
          createUserAddress: false,
          orderSelection: { [orderObjectId]: [parsedBatch.id] },
          source: '',
          trackingReference,
        }),
      );
    }

    yield put(ordersActions.createOrderReturnBatchEntrySuccess());
  } catch (error) {
    observability.errorEvent('RETURN_WORKFLOW_DRAWER_OPEN', {
      errorMessage: 'Something went wrong, please refresh and try again',
    });
    yield put(ordersActions.createOrderReturnLabelError());
    newRelic.noticeError('Failed to create return label', { error });
  }
}

export default [
  ordersPollWatcher(),
  batchEntriesPollWatcher(),
  takeEvery(ActionKeys.ORDER_FETCH, fetchOrder),
  takeLatest(ActionKeys.ORDERS_FETCH, fetchOrders),
  takeLatest(ActionKeys.ORDERS_SYNC, syncOrders),
  takeLeading(ActionKeys.ORDERS_GET_PACKING_SLIP, getOrderPackingSlip),
  takeEvery(ActionKeys.ORDER_HIDE, hideOrder),
  takeEvery(ActionKeys.ORDERS_UPDATE_PARCEL, updateParcel),
  takeEvery(ActionKeys.ORDER_BATCH_ENTRY_FETCH, fetchOrderBatchEntry),
  debounce(100, ActionKeys.ORDERS_COUNT_FETCH, countOrders),
  takeLatest(ActionKeys.ORDERS_PATCH_BATCH_ENTRIES, patchBatchEntries),
  takeLatest(ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS, updateBatchEntriesExtras),
  takeLatest(ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS, patchUpdateBatchEntriesExtras),
  takeLatest(
    ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_PACKING_SLIP_NOTE,
    patchUpdateBatchEntriesExtrasPackingSlipNote,
  ),
  takeEvery(ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_ADDRESS, updateBatchEntriesAddress),
  takeEvery(ActionKeys.ORDERS_CREATE_ANOTHER_LABEL, batchEntriesRegen),
  takeLatest(ActionKeys.ORDERS_BATCH_ENTRY_RETRY_SHIPMENT, retryShipment),
  takeLatest(ActionKeys.ORDERS_CHECKOUT_SEND_EMAIL_NOTIFICATIONS, checkoutSendEmailNotification),
  takeLatest(ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE, updateBatchEntriesShipmentDate),
  takeLatest(
    ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN,
    updateBatchEntriesServiceLevelToken,
  ),
  takeLatest(ActionKeys.BATCH_ENTRY_RULES_FETCH, fetchBatchEntryRules),
  takeLatest(ActionKeys.ORDER_UNDO_SPLIT, undoSplitOrder),
  takeLatest(
    ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS,
    createPartialFulfillmentRequests,
  ),
  takeLatest(ActionKeys.ORDERS_START_CHECKOUT, startCheckout),
  takeLatest(ActionKeys.ORDERS_RESTART_CHECKOUT, reStartCheckout),
  takeLatest(ActionKeys.ORDERS_RETRY_CHECKOUT, retryCheckout),
  takeLatest(ActionKeys.ORDERS_FETCH_SUCCESS, fetchOrderTags),
  takeLatest(ActionKeys.ORDER_CREATE_RETURN_LABEL, createReturnLabel),
  takeLatest(ActionKeys.ORDERS_LOAD_AND_SELECT, loadAndSelectOrders),
  takeLatest(ActionKeys.ORDERS_SELECT_ORDER, loadDataForSelectedOrders),
  // Commenting out the following action dispatcher due to potential system outage from increased
  // put calls originating from targetted saga
  // takeLatest(ActionKeys.ORDERS_BATCH_ENTRIES_POLL_END, updateExpiredShipments),
];
